import { AssetStatus } from '@electricjs/core_entity-client';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import { Chip, ColorsV2, Flex, Text } from '@electricjs/arc';

type AssetStatusChipColor = {
  textColor: string;
  backgroundColor: string;
};

const assetStatusColorMap: Record<AssetStatus, AssetStatusChipColor> = {
  [AssetStatus.InUse]: {
    backgroundColor: ColorsV2.INFO_LIGHTEST,
    textColor: ColorsV2.INFO_DARKEST,
  },
  [AssetStatus.Available]: {
    backgroundColor: ColorsV2.SUCCESS_LIGHTEST,
    textColor: ColorsV2.SUCCESS_DARKEST,
  },
  [AssetStatus.UnderRepair]: {
    backgroundColor: ColorsV2.WARNING_LIGHTEST,
    textColor: ColorsV2.WARNING_DARKEST,
  },
  [AssetStatus.Donated]: {
    backgroundColor: ColorsV2.INFO_LIGHTEST,
    textColor: ColorsV2.INFO_DARKEST,
  },
  [AssetStatus.Sold]: {
    backgroundColor: ColorsV2.INFO_LIGHTEST,
    textColor: ColorsV2.INFO_DARKEST,
  },
  [AssetStatus.Disposed]: {
    backgroundColor: ColorsV2.INFO_LIGHTEST,
    textColor: ColorsV2.INFO_DARKEST,
  },
  [AssetStatus.Missing]: {
    backgroundColor: ColorsV2.ERROR_LIGHTEST,
    textColor: ColorsV2.ERROR_DARKEST,
  },
  [AssetStatus.Archived]: {
    backgroundColor: ColorsV2.GRAY_LIGHTER,
    textColor: ColorsV2.GRAY_DARKEST,
  },
  [AssetStatus.InTransit]: {
    backgroundColor: ColorsV2.INFO_LIGHTEST,
    textColor: ColorsV2.INFO_DARKEST,
  },
  [AssetStatus.WarehouseIntake]: {
    backgroundColor: ColorsV2.INFO_LIGHTEST,
    textColor: ColorsV2.INFO_DARKEST,
  },
};

export const assetStatusTranslationKeys: Record<AssetStatus, string> = {
  [AssetStatus.InUse]: 'status.inUse',
  [AssetStatus.Available]: 'status.available',
  [AssetStatus.UnderRepair]: 'status.underRepair',
  [AssetStatus.Donated]: 'status.donated',
  [AssetStatus.Sold]: 'status.sold',
  [AssetStatus.Disposed]: 'status.disposed',
  [AssetStatus.Missing]: 'status.missing',
  [AssetStatus.Archived]: 'status.archived',
  [AssetStatus.InTransit]: 'status.inTransit',
  [AssetStatus.WarehouseIntake]: 'status.warehouseIntake',
};

// Translates a status coming from real asset data (as opposed to a value
// drawn from the AssetStatus enum itself, e.g. in a dropdown). Falls back to
// the raw status — instead of an "assets:undefined"-shaped string or a raw
// i18next key — if the backend ever reports a status this map hasn't caught
// up to yet, or if a locale is missing the translation entry.
export const getTranslatedAssetStatusLabel = (
  status: AssetStatus,
  t: TFunction
): string => {
  const translationKey = assetStatusTranslationKeys[status];
  if (!translationKey) return status;
  return t(`assets:${translationKey}`, { defaultValue: status });
};

type AssetStatusChipProps = {
  status: AssetStatus;
  chipSize?: 'small' | 'medium';
};

export const AssetStatusChip = ({
  status,
  chipSize = 'small',
}: AssetStatusChipProps) => {
  const { t } = useTranslation('assets');

  return (
    <Chip
      id="asset-status-chip"
      variant={chipSize}
      backgroundColor={assetStatusColorMap[status]?.backgroundColor}>
      <Text variant="legal" color={assetStatusColorMap[status]?.textColor}>
        {t(assetStatusTranslationKeys[status])}
      </Text>
    </Chip>
  );
};

export const DeviceLockedChip = () => {
  const { t } = useTranslation('assets');

  return (
    <Chip
      id="asset-status-chip"
      variant="small"
      icon="lock"
      textColor={ColorsV2.INFO_DARKEST}
      backgroundColor={ColorsV2.INFO_LIGHTEST}>
      <Text variant="legal" color={ColorsV2.INFO_DARKEST}>
        {t('assetsPage.locked')}
      </Text>
    </Chip>
  );
};

type AssetStatusColumnProps = {
  status: AssetStatus;
};

export const AssetStatusColumn = ({ status }: AssetStatusColumnProps) => {
  return (
    <Flex hAlignContent="left">
      <AssetStatusChip status={status} />
    </Flex>
  );
};
